import React, { useState, useEffect, useCallback } from 'react';
import { Navigate, Routes, Route, useLocation } from 'react-router-dom';
import Navigation from './components/Navigation';
import Dashboard from './components/Dashboard';
import Login from './components/Login';
import TicketList from './components/TicketList';
import TicketDetail from './components/TicketDetail';
import ReviewQueue from './components/ReviewQueue';
import KnowledgeBase from './components/KnowledgeBase';
import { clearStoredAuth, helpdeskAPI } from './services/api';
import ApplicationContext from './contexts/ApplicationContext';
import AuthContext from './contexts/AuthContext';
import './styles/index.css';
import './App.css';

const ProtectedRoute = ({ isAuthenticated, children }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const location = useLocation();
  const [currentApplication, setCurrentApplication] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);

  const loadApplications = useCallback(async () => {
    const response = await helpdeskAPI.getApplications();
    const loadedApplications = response.data.results || response.data || [];
    const preferredSlug = process.env.REACT_APP_HELPDESK_APP_SLUG || 'erp';

    setApplications(loadedApplications);

    if (loadedApplications.length === 0) {
      setCurrentApplication(null);
      return;
    }

    const userApplicationId = user?.helpdesk_application_user?.application;
    const userScopedApplication =
      loadedApplications.find((app) => app.id === userApplicationId) || null;

    const preferredApplication =
      userScopedApplication ||
      loadedApplications.find((app) => app.slug === preferredSlug) ||
      loadedApplications[0];

    setCurrentApplication(preferredApplication);
  }, [user]);

  const refreshCurrentUser = useCallback(async () => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      setUser(null);
      setAuthLoading(false);
      setLoading(false);
      return null;
    }

    try {
      const response = await helpdeskAPI.getCurrentUser();
      setUser(response.data);
      return response.data;
    } catch (error) {
      clearStoredAuth();
      setUser(null);
      return null;
    } finally {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshCurrentUser();
  }, [refreshCurrentUser]);

  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      setApplications([]);
      setCurrentApplication(null);
      setLoading(false);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        await loadApplications();
      } catch (error) {
        console.error('Error loading applications:', error);
        setApplications([]);
        setCurrentApplication(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [loadApplications, user]);

  const login = async (credentials) => {
    const response = await helpdeskAPI.login(credentials);
    localStorage.setItem('access_token', response.data.access);
    localStorage.setItem('refresh_token', response.data.refresh);
    localStorage.removeItem('helpdesk_application_key');
    localStorage.removeItem('helpdesk_application_user_id');
    localStorage.removeItem('helpdesk_external_user_id');

    try {
      setAuthLoading(true);
      const currentUser = await refreshCurrentUser();
      if (currentUser) {
        await loadApplications();
      }
    } finally {
      setAuthLoading(false);
      setLoading(false);
    }
  };

  const logout = () => {
    clearStoredAuth();
    setUser(null);
    setApplications([]);
    setCurrentApplication(null);
  };

  if (authLoading || (loading && localStorage.getItem('access_token'))) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const isTicketDetailRoute = /^\/tickets\/[^/]+$/.test(location.pathname);
  const appContentClassName = isTicketDetailRoute
    ? 'helpdesk-route-shell helpdesk-route-shell--flush'
    : 'helpdesk-route-shell';

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: Boolean(user),
      authLoading,
      login,
      logout,
      refreshCurrentUser,
    }}>
      <ApplicationContext.Provider value={{
        currentApplication,
        setCurrentApplication,
        applications
      }}>
        <div className="App">
          {user && <Navigation />}
          <div className={appContentClassName}>
            <Routes>
              <Route
                path="/login"
                element={user ? <Navigate to="/" replace /> : <Login />}
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute isAuthenticated={Boolean(user)}>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tickets"
                element={
                  <ProtectedRoute isAuthenticated={Boolean(user)}>
                    <TicketList />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/tickets/:id"
                element={
                  <ProtectedRoute isAuthenticated={Boolean(user)}>
                    <TicketDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/review-queue"
                element={
                  <ProtectedRoute isAuthenticated={Boolean(user)}>
                    <ReviewQueue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/knowledge-base"
                element={
                  <ProtectedRoute isAuthenticated={Boolean(user)}>
                    <KnowledgeBase />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to={user ? "/" : "/login"} replace />} />
            </Routes>
          </div>
        </div>
      </ApplicationContext.Provider>
    </AuthContext.Provider>
  );
}

export default App;
